import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, useMemo } from "react";
import clsx from "clsx";
import { isEmpty, isFunction } from "@util/utils";
import { Grid, Box } from "@mui/material";
import { FormLabel, Select, OutlinedInput, MenuItem, FormHelperText } from "@curves/ui-base";

// 資料來源
import { default as addressData } from "./addressDataTw";

const EMPTY_ADDRESS_RESULT = {
	postCode: "",
	cityIndex: "",
	districtIndex: "",
	districtOptions: [],
	postCodeOptions: [],
};

// 模組層級的空陣列常數：作為 props 預設值
const EMPTY_LIST = [];

const parseByAddress = addressString => {
	const group = {
		city: "",
		postCode: "",
		district: "",
		others: "",
	};
	const reg = /(?<postCode>(^\d{5}|^\d{3})?)(?<city>(\D{1,2}[縣市])?)(?<district>(\D+?(市區|鎮區|鎮市|[鄉鎮市區]))?)(?<others>.+)/gm;
	const _regObj = reg.exec(addressString);

	return _regObj ? _regObj.groups : group;
};

const AddressField = React.forwardRef((props, ref) => {
	const {
		className,
		label,
		postCode: postCodeProps = "",
		address: addressProps = "",
		readOnly,
		addressPlaceholder = "請輸入",
		required: requiredProps = false,
		isError: errorProps = false,
		helperText: helperTextProps = "此選項為必填，不得為空值",
		disabled: disabledProps = false,
		tips,
		containerSpacing = 4,
		excludedCounties = EMPTY_LIST,
		excludedDistricts = EMPTY_LIST,
		addressNormalizer,
		onPostCodeChange,
	} = props;
	const { counties: cityOptions, districts: districtOptions, zipCodeObj } = addressData;
	// 第 1 層：下拉排除規則（呼叫端可指定要排除的縣市/鄉鎮）
	const excludedCountySet = useMemo(() => new Set(excludedCounties), [excludedCounties]);
	const excludedDistrictSet = useMemo(() => new Set(excludedDistricts), [excludedDistricts]);
	const visibleCityOptions = useMemo(() => {
		if (!Array.isArray(cityOptions)) return [];
		return cityOptions.map((name, index) => ({ name, index })).filter(item => !excludedCountySet.has(item.name));
	}, [cityOptions, excludedCountySet]);

	const isExcludedCityIndex = useCallback(
		cityIndex => {
			const cityName = cityOptions[cityIndex];
			return excludedCountySet.has(cityName);
		},
		[cityOptions, excludedCountySet],
	);

	const getFilteredDistrictData = useCallback(
		(rawDistrictOptions = []) => {
			const districtNames = rawDistrictOptions[0] || [];
			const postCodes = rawDistrictOptions[1] || [];
			const filteredDistrictNames = [];
			const filteredPostCodes = [];
			districtNames.forEach((districtName, districtIndex) => {
				if (!excludedDistrictSet.has(districtName)) {
					filteredDistrictNames.push(districtName);
					filteredPostCodes.push(postCodes[districtIndex]);
				}
			});
			return [filteredDistrictNames, filteredPostCodes];
		},
		[excludedDistrictSet],
	);

	const isEmptyAddressResult = useCallback(result => isEmpty(result?.cityIndex) && isEmpty(result?.districtIndex) && isEmpty(result?.districtOptions) && isEmpty(result?.postCodeOptions), []);

	const [isError, setError] = useState(errorProps);
	const [helperText, setHelperText] = useState(helperTextProps);
	const [required, setRequired] = useState(requiredProps);
	const [data, setData] = useState({
		postCode: "",
		cityIndex: "",
		districtIndex: "",
		others: "",
		districtOptions: [],
		postCodeOptions: [],
	});
	const postCodeRef = useRef();
	const cityRef = useRef();
	const districtRef = useRef();
	const otherRef = useRef();
	const normalizeAddressText = useCallback(
		text => {
			const sourceText = typeof text === "string" ? text : "";
			return typeof addressNormalizer === "function" ? addressNormalizer(sourceText) : sourceText;
		},
		[addressNormalizer],
	);

	// 取得對應 鄉鎮市區 district options
	const getDistrictsOptions = useCallback(
		cityIndex => {
			return districtOptions[cityIndex];
		},
		[districtOptions],
	);

	const getRawDistrictDataByCityIndex = useCallback(
		cityIndex => {
			const options = getDistrictsOptions(cityIndex);
			return {
				districtNames: options?.[0] || [],
				postCodes: options?.[1] || [],
			};
		},
		[getDistrictsOptions],
	);

	const isExcludedDistrictIndex = useCallback(
		(cityIndex, districtIndex) => {
			const { districtNames } = getRawDistrictDataByCityIndex(cityIndex);
			const districtName = districtNames[districtIndex];
			return excludedDistrictSet.has(districtName);
		},
		[getRawDistrictDataByCityIndex, excludedDistrictSet],
	);

	const isExcludedPostCode = useCallback(
		postCode => {
			let zipCode = postCode || "";
			if (zipCode.length > 3) zipCode = zipCode.slice(0, 3);
			const addressObj = zipCodeObj.hasOwnProperty(zipCode) ? zipCodeObj[zipCode] : null;
			if (isEmpty(addressObj)) return false;
			return isExcludedCityIndex(addressObj.city) || isExcludedDistrictIndex(addressObj.city, addressObj.district);
		},
		[zipCodeObj, isExcludedCityIndex, isExcludedDistrictIndex],
	);

	const getAddressByPostCode = useCallback(
		postCode => {
			let result = {
				...EMPTY_ADDRESS_RESULT,
				postCode,
			};
			let zipCode = postCode || "";
			if (zipCode.length > 3) zipCode = zipCode.slice(0, 3);
			const addressObj = zipCodeObj.hasOwnProperty(zipCode) ? zipCodeObj[zipCode] : null;

			if (isEmpty(addressObj)) return result;

			// 第 2 層：郵遞區號若對應到排除區域，直接視為無效（不帶入縣市鄉鎮）
			if (isExcludedCityIndex(addressObj.city) || isExcludedDistrictIndex(addressObj.city, addressObj.district)) {
				return result;
			}

			const { districtNames, postCodes } = getRawDistrictDataByCityIndex(addressObj.city);
			const [filteredDistrictNames, filteredPostCodes] = getFilteredDistrictData([districtNames, postCodes]);
			result.districtOptions = filteredDistrictNames;
			result.postCodeOptions = filteredPostCodes;
			result.cityIndex = addressObj.city;
			result.districtIndex = filteredPostCodes.findIndex(zip => String(zip) === String(zipCode));
			return result;
		},
		[zipCodeObj, isExcludedCityIndex, isExcludedDistrictIndex, getRawDistrictDataByCityIndex, getFilteredDistrictData],
	);

	const getAddressByCity = useCallback(
		cityIndex => {
			let result = { ...EMPTY_ADDRESS_RESULT };

			if (cityIndex !== -1) {
				if (isExcludedCityIndex(cityIndex)) {
					return result;
				}
				let districtOptions = getDistrictsOptions(cityIndex);
				result.cityIndex = cityIndex;
				if (!isEmpty(districtOptions)) {
					const [filteredDistrictNames, filteredPostCodes] = getFilteredDistrictData(districtOptions);
					result.districtOptions = filteredDistrictNames;
					result.postCodeOptions = filteredPostCodes;
				}
			}

			return result;
		},
		[isExcludedCityIndex, getDistrictsOptions, getFilteredDistrictData],
	);

	const getAddressByDistrict = (districtIndex, postCodeOptions) => {
		let result = {
			postCode: "",
			districtIndex: "",
		};
		if (districtIndex !== -1) {
			let zipCode = postCodeOptions[districtIndex];
			if (zipCode) result.postCode = zipCode;
			result.districtIndex = districtIndex;
		}
		return result;
	};

	const handleAddressDefaultValue = useCallback(
		(addressString, postCodeString) => {
			const { postCode: zipCode, city: cityText, district: districtText, others } = parseByAddress(addressString);
			let result = {
				postCode: zipCode || postCodeString,
				others: normalizeAddressText(others),
			};

			// 初始化優先順序：完整地址字串（縣市/鄉鎮）> 郵遞區號
			if (cityText) {
				let cityIndex = cityOptions.findIndex(name => name === cityText);
				if (cityIndex === -1) {
					// 錯誤資料塞到input 顯示
					result.others = normalizeAddressText(addressString);
				} else if (isExcludedCityIndex(cityIndex)) {
					// 排除縣市時，不帶入下拉選值
					result = {
						...result,
						...EMPTY_ADDRESS_RESULT,
						others: normalizeAddressText(addressString),
					};
				} else {
					result = {
						...result,
						...getAddressByCity(cityIndex),
					};
					if (districtText) {
						let districtIndex = result.districtOptions.findIndex(name => name === districtText);
						if (districtIndex === -1) {
							// 錯誤資料塞到input 顯示
							result.others = normalizeAddressText(districtText + others);
						} else {
							result = {
								...result,
								...getAddressByDistrict(districtIndex, result.postCodeOptions),
							};
						}
					}
				}
			} else if (result.postCode && result.postCode.length >= 3) {
				const postCodeResult = getAddressByPostCode(result.postCode);
				if (isEmptyAddressResult(postCodeResult)) {
					result = {
						...result,
						...EMPTY_ADDRESS_RESULT,
						others: normalizeAddressText(addressString || others),
					};
				} else {
					result = {
						...result,
						...postCodeResult,
					};
				}
			} else {
				result.others = normalizeAddressText(addressString);
			}

			setData(prev => ({
				...prev,
				...result,
			}));
		},
		[cityOptions, normalizeAddressText, isExcludedCityIndex, getAddressByCity, getAddressByPostCode, isEmptyAddressResult],
	);

	const handleChangePostcode = e => {
		const { others } = parseByAddress(data.others);
		const value = e.target.value;
		let result = getAddressByPostCode(value);
		const hasExcludedPostCode = value && value.length >= 3 && isExcludedPostCode(value);

		if (hasExcludedPostCode) {
			setError(true);
			setHelperText("此郵遞區號不支援配送");
		} else {
			setError(errorProps);
			setHelperText(helperTextProps);
		}

		setData(prev => ({
			...prev,
			...result,
			others,
		}));

		isFunction(onPostCodeChange) && onPostCodeChange(value);
	};

	const handleChangeCity = e => {
		const value = e.target.value;
		let result = getAddressByCity(value);
		setError(errorProps);
		setHelperText(helperTextProps);
		setData(prev => ({
			...prev,
			...result,
		}));
	};

	const handleChangeDistrict = e => {
		const { others } = parseByAddress(data.others);
		const value = e.target.value;
		const result = getAddressByDistrict(value, data.postCodeOptions);
		setError(errorProps);
		setHelperText(helperTextProps);

		setData(prev => ({
			...prev,
			...result,
			others,
		}));
	};

	const handleChangeOther = e => {
		const value = e.target.value;
		setError(errorProps);
		setHelperText(helperTextProps);
		setData(prev => ({
			...prev,
			others: normalizeAddressText(value),
		}));
	};

	useEffect(() => {
		if (!addressProps) return;
		// 僅在外部傳入的地址／郵遞區號真正改變時才重新初始化，
		// 不可依賴 handleAddressDefaultValue 的函式參考（其每次 render 都可能變動），否則會在編輯時不斷把欄位重設回原始值而無法輸入
		handleAddressDefaultValue(addressProps, postCodeProps);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addressProps, postCodeProps]);

	useEffect(() => {
		if (data.cityIndex === "" || data.cityIndex === null) return;
		const selectedCountyName = cityOptions[data.cityIndex];
		if (!excludedCountySet.has(selectedCountyName)) return;

		// 防呆：若外部資料帶入被排除縣市，主動清空目前選擇避免殘留
		setData(prev => ({
			...prev,
			cityIndex: "",
			districtIndex: "",
			districtOptions: [],
			postCodeOptions: [],
			postCode: "",
		}));
	}, [data.cityIndex, cityOptions, excludedCountySet]);

	useEffect(() => {
		setError(errorProps);
	}, [errorProps]);

	useEffect(() => {
		setHelperText(helperTextProps);
	}, [helperTextProps]);

	useEffect(() => {
		if (readOnly) {
			setRequired(false);
		}
	}, [readOnly]);

	useImperativeHandle(ref, () => ({
		setErrorState: (error, text) => {
			setError(error);
			setHelperText(text !== undefined ? text : helperTextProps);
		},
		getResult: () => {
			let address = "";
			let postCode = "";

			if (cityRef && cityRef.current && cityRef.current.value) address = address + cityOptions[cityRef.current.value];

			if (districtRef && districtRef.current && districtRef.current.value) address = address + data.districtOptions[districtRef.current.value];

			if (otherRef && otherRef.current && otherRef.current.value) address = address + normalizeAddressText(otherRef.current.value);

			if (postCodeRef && postCodeRef.current && postCodeRef.current.value) postCode = postCodeRef.current.value;

			return {
				postCode,
				address,
			};
		},
		// 判斷目前郵遞區號是否屬於排除（離島）區域
		hasExcludedPostCode: () => {
			const postCode = postCodeRef.current?.value || "";
			if (!postCode || postCode.length < 3) return false;
			return isExcludedPostCode(postCode);
		},
		isError: () => {
			let isError = false;
			let field = [];
			let helperText;

			if (required) {
				let cityError = cityRef && cityRef.current && isEmpty(cityRef.current.value);
				let districtError = districtRef && districtRef.current && isEmpty(districtRef.current.value);
				let otherError = otherRef && otherRef.current && isEmpty(otherRef.current.value);

				if (cityError) {
					isError = cityError;
					field.push("鄉鎮[市]區");
				}
				if (districtError) {
					isError = districtError;
					field.push("鄉鎮[市]區");
				}
				if (otherError) {
					isError = otherError;
					helperText = helperTextProps;
				} else {
					if (field.length > 0) {
						helperText = field.join("、");
						helperText = helperText + "為必填欄位";
					}
				}
			}
			setError(isError);
			setHelperText(helperText);
		},
		resetValue: (address, postCode) => {
			if (address && typeof address === "string") handleAddressDefaultValue(address, postCode);
		},
	}));

	return (
		<Box className={clsx("address-field", className)}>
			{label && <FormLabel required={required}>{label}</FormLabel>}
			<Box className="address ">
				<Grid container spacing={containerSpacing}>
					<Grid item xs={4}>
						<OutlinedInput
							className={"address-item"}
							type={"number"}
							inputRef={postCodeRef}
							value={data.postCode}
							placeholder={!disabledProps ? "郵遞區號 三碼" : ""}
							onChange={handleChangePostcode}
							readOnly={readOnly}
							isError={isError}
							disabled={disabledProps}
							fullWidth
						/>
					</Grid>
					<Grid item xs={4}>
						<Select
							className={"address-item"}
							inputRef={cityRef}
							value={data.cityIndex}
							onChange={handleChangeCity}
							required={required}
							readOnly={readOnly}
							isError={isError}
							disabled={disabledProps}
							displayEmpty={!disabledProps}
							fullWidth
						>
							<MenuItem disabled value="">
								<em>縣市</em>
							</MenuItem>
							{Array.isArray(visibleCityOptions) &&
								visibleCityOptions.map(({ name, index }) => (
									<MenuItem key={name} value={index}>
										{name}
									</MenuItem>
								))}
						</Select>
					</Grid>
					<Grid item xs={4}>
						<Select
							name={"district"}
							inputRef={districtRef}
							value={data.districtIndex}
							className={"address-item"}
							fullWidth
							onChange={handleChangeDistrict}
							required={required}
							readOnly={readOnly}
							isError={isError}
							disabled={disabledProps}
							displayEmpty={!disabledProps}
						>
							<MenuItem disabled value="">
								<em>鄉鎮[市]區</em>
							</MenuItem>
							{Array.isArray(data.districtOptions) &&
								data.districtOptions.map((name, i) => (
									<MenuItem key={name} value={i}>
										{name}
									</MenuItem>
								))}
						</Select>
					</Grid>
					<Grid item xs={12}>
						<div className={"address-item"}>
							<OutlinedInput
								name={"others"}
								inputRef={otherRef}
								value={data.others}
								placeholder={!disabledProps ? addressPlaceholder : ""}
								onChange={handleChangeOther}
								required={required}
								readOnly={readOnly}
								isError={isError}
								disabled={disabledProps}
								fullWidth
							/>
						</div>
					</Grid>
				</Grid>
			</Box>
			{!isEmpty(tips) && <FormHelperText className={"tips"}>{tips}</FormHelperText>}
			{isError && <FormHelperText isError={isError}>{helperText}</FormHelperText>}
		</Box>
	);
});

export default AddressField;
